import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const RefundPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />

      <main className="flex-grow">
        {/* Header */}
        <div className="bg-black text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold mb-4">Refund Policy</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-gray-600 mb-8">
            Please review the Refund Policy below. All deadlines are in the <strong>Central Time Zone</strong>.
          </p>

          {/* eCredits */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">eCredits</h2>
            <p className="text-gray-700 mb-3">
              An eCredit will allow registrants to withdraw and receive credit directly to their tournament account.
              eCredits will automatically be applied to any future Champions Classic event registrations.
            </p>
            <p className="text-gray-700">
              Please note that all eCredits are valid for <strong>one year from date of issue</strong> and once issued
              may not be redeemed as cash, credit card refund, bank transfer, or check refund.
            </p>
          </section>

          {/* Standard Registration */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Standard &amp; Sponsor Golfer Registrations</h2>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Withdraw request submitted 22 or more days prior to the event:
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>90% of the registration fee will be refunded to your credit card, <strong>OR</strong></li>
                <li>100% eCredit to your account</li>
                <li>Registrant will be removed from the event</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Withdraw request submitted 5–21 days prior to the event:
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>75% of the registration fee will be refunded to your credit card, <strong>OR</strong></li>
                <li>85% eCredit to your account</li>
                <li>Registrant will be removed from the event</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-lg p-6 mb-4 border border-red-200">
              <h3 className="text-lg font-semibold text-red-800 mb-3">
                Withdraw request submitted LESS THAN 5 DAYS prior to the event:
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>No refund available</li>
                <li>Registrant will be marked as withdrawn (WD)</li>
              </ul>
            </div>
          </section>

          {/* Multi-Day Events */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Multi-Day Events</h2>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Withdraw request submitted 45 or more days prior to the event:
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>90% of the registration fee will be refunded to your credit card, <strong>OR</strong></li>
                <li>100% eCredit to your account</li>
                <li>Registrant will be removed from the event</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Withdraw request submitted 22–44 days prior to the event:
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>75% of the registration fee will be refunded to your credit card, <strong>OR</strong></li>
                <li>85% eCredit to your account</li>
                <li>Registrant will be removed from the event</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Withdraw request submitted 8–21 days prior to the event:
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>50% of the registration fee will be refunded to your credit card, <strong>OR</strong></li>
                <li>60% eCredit to your account</li>
                <li>Registrant will be removed from the event</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-lg p-6 mb-4 border border-red-200">
              <h3 className="text-lg font-semibold text-red-800 mb-3">
                Withdraw request submitted LESS THAN 8 DAYS prior to the event:
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>No refund available</li>
                <li>Registrant will be marked as withdrawn (WD)</li>
              </ul>
            </div>
          </section>

          {/* No Show Policy */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Show Policy</h2>
            <p className="text-gray-700 mb-3">
              Registrants who are no longer able to attend an event must email us and notify the tournament committee.
              Those who fail to notify the Champions Classic organizing committee are documented as No Shows.
              A No Show leaves a hole in the field and prevents other participants from entering the event off the waiting list.
            </p>
            <p className="text-gray-700">
              A first offense No Show will receive a warning and is documented.
              A second No Show during the calendar year will result in a loss of all Priority Status for future Champions Classic events.
            </p>
          </section>

          {/* Medical/Emergency Policy */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Medical / Emergency Policy</h2>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Medical/Emergency request submitted 5 or more days prior to the event:
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Full eCredit will be issued</li>
                <li>Registrant will be removed from the event</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Medical/Emergency request submitted less than 5 days prior to the event:
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>eCredit minus a $15 processing fee will be issued</li>
                <li>Registrant will be marked as withdrawn (WD)</li>
              </ul>
            </div>

            <p className="text-gray-700 mb-4">
              In order for a refund to be issued, documentation such as a doctor's note of medical or family emergency
              must be emailed to{' '}
              <a href="mailto:charityevent@crashchampions.com" className="text-red-600 hover:underline">
                charityevent@crashchampions.com
              </a>{' '}
              no later than one week after the first round of the tournament.
            </p>

            <div className="bg-gray-100 rounded-lg p-6">
              <p className="text-gray-900 font-semibold mb-3">All Medical/Emergency withdrawal requests must include:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Email Subject: <strong>Medical Withdrawal</strong></li>
                <li>Order / Confirmation Number</li>
                <li>Participant's Name</li>
                <li>Registration Type</li>
                <li>Event Location</li>
                <li>Event Date</li>
                <li>Reason for Withdrawal</li>
              </ul>
            </div>
          </section>

          {/* Event Cancelled / Postponed */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Event Cancelled, Postponed, or Rescheduled</h2>
            <p className="text-gray-700 mb-4">
              All efforts will be made to complete tournament rounds. If a tournament or tournament rounds must be
              cancelled and 9-hole players have completed five holes, and 18-hole players have completed nine holes,
              the round/tournament will be deemed sufficient and no refunds will be given. For multi-day events,
              events are considered completed after one round has been played. Play will not extend past the date of
              the Final Round.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">If an event is cancelled and will not be rescheduled:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Customers will receive a full refund or a full eCredit</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">If an event is postponed and rescheduled:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Registration fees will automatically be transferred to the rescheduled date</li>
                <li>If you are unable to attend the new event date, please follow the standard withdraw procedure</li>
                <li>Customers will receive a full refund or a full eCredit</li>
                <li>Withdraw requests must be submitted 5 or more days prior to the new event date to receive a refund</li>
              </ul>
            </div>
          </section>

          {/* Failure to Follow Cancellation Procedure */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Failure to Follow Cancellation Procedure</h2>
            <p className="text-gray-700">
              Once an event begins, a withdrawal for any reason other than illness, injury, or family emergency may
              result in the suspension of participation in any future Champions Classic events for a period of one year.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-black text-white rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Questions About Our Refund Policy?</h2>
            <p className="text-gray-300 mb-4">
              Contact the Champions Classic tournament committee for any questions regarding withdrawals or refunds.
            </p>
            <a
              href="mailto:charityevent@crashchampions.com"
              className="inline-block bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              Contact Us
            </a>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RefundPolicyPage;
